import { z } from 'zod';
import { storedHttpUrlSchema } from '../safe-url.js';

// ---------------------------------------------------------------------------
// RUF-118 — Market Comp Validation CRUD DTOs
//
// Documents the contract between the API (apps/api/src/routes/skus.ts) and
// the UI (apps/web/src/routes/skus_.$skuId.tsx) for the Market Data section.
// ---------------------------------------------------------------------------

/** A canonical market_comp row as returned by GET /api/skus/:skuId/market-comps */
export const marketCompValidationRowSchema = z.object({
  id: z.string().uuid(),
  skuId: z.string().uuid().nullable(),
  sourceUrl: storedHttpUrlSchema.nullable(),
  sourceId: z.string(),
  retailerName: z.string().nullable(),
  conditionBand: z.string().nullable(),
  observedPriceUsd: z.number(),
  lotSize: z.number().int().positive().nullable(),
  notes: z.string().nullable(),
  observedAt: z.string().datetime(),
  skuMatchStatus: z.string(),
  importBatchId: z.string().uuid().nullable(),
  excludedAt: z.string().datetime().nullable(),
  /** True when the unit price falls outside the 90-day IQR fence for this SKU */
  isOutlier: z.boolean(),
});
export type MarketCompValidationRow = z.infer<typeof marketCompValidationRowSchema>;

/** An unpromoted market_comp_staging row */
export const stagingValidationRowSchema = z.object({
  id: z.string().uuid(),
  sourceCodeRaw: z.string().nullable(),
  urlRaw: z.string().nullable(),
  observedPriceRaw: z.string().nullable(),
  conditionRaw: z.string().nullable(),
  lotSizeRaw: z.string().nullable(),
  rowErrors: z.array(z.string()).nullable(),
  promotedAt: z.string().datetime().nullable(),
  promotedMarketCompId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  importBatchId: z.string().uuid(),
  retailerNameRaw: z.string().nullable(),
});
export type StagingValidationRow = z.infer<typeof stagingValidationRowSchema>;

/** A sku_oem_spec row joined with scrape_source metadata */
export const oemSpecValidationRowSchema = z.object({
  id: z.string().uuid(),
  skuId: z.string().uuid(),
  vendor: z.string().nullable(),
  sourceUrl: z.string().nullable(),
  scrapedAt: z.string().datetime(),
  vramGb: z.number().nullable(),
  tdpWatts: z.number().nullable(),
  architecture: z.string().nullable(),
  interconnectType: z.string().nullable(),
  formFactor: z.string().nullable(),
  maxGpuCount: z.number().nullable(),
  pcieGen: z.number().nullable(),
  powerSupplyWatts: z.number().nullable(),
  cpuCores: z.number().nullable(),
  cpuThreads: z.number().nullable(),
  baseClockGhz: z.coerce.number().nullable(),
  boostClockGhz: z.coerce.number().nullable(),
  cpuSocket: z.string().nullable(),
  cpuMaxSockets: z.number().nullable(),
  l3CacheMb: z.coerce.number().nullable(),
  rackUnits: z.number().nullable(),
  maxPowerKw: z.coerce.number().nullable(),
  totalGpuMemoryGb: z.coerce.number().nullable(),
  gpuModel: z.string().nullable(),
  memoryType: z.string().nullable(),
  specFacts: z.record(z.union([z.string(), z.number(), z.boolean()])),
});
export type OemSpecValidationRow = z.infer<typeof oemSpecValidationRowSchema>;

/** Response shape for GET /api/skus/:skuId/market-comps */
export const marketCompsResponseSchema = z.object({
  comps: z.array(marketCompValidationRowSchema),
  staging: z.array(stagingValidationRowSchema),
  oemSpecs: z.array(oemSpecValidationRowSchema),
  specDisplaySchema: z.unknown(),
});
export type MarketCompsResponse = z.infer<typeof marketCompsResponseSchema>;

/** Body for PATCH /api/skus/:skuId/market-comps/:compId */
export const updateMarketCompBodySchema = z.object({
  observedPriceUsd: z.number().positive().optional(),
  conditionBand: z.string().optional(),
  lotSize: z.number().int().positive().nullable().optional(),
  retailerName: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type UpdateMarketCompBody = z.infer<typeof updateMarketCompBodySchema>;

/** Response for PATCH /api/skus/:skuId/market-comps/:compId */
export const updateMarketCompResponseSchema = z.object({
  comp: marketCompValidationRowSchema,
  isOutlierWarning: z.boolean(),
});
export type UpdateMarketCompResponse = z.infer<typeof updateMarketCompResponseSchema>;
